import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import apiService from '../services/api.service';

interface ConnectAccountStatus {
  organization_id: string;
  stripe_account_id: string | null;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  onboarding_completed: boolean;
  details_submitted: boolean;
}

const StripeOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<ConnectAccountStatus | null>(null);

  useEffect(() => {
    loadAccountStatus();
  }, []);

  const loadAccountStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get('/api/v1/stripe/connect/status');
      setAccountStatus(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Aucun compte Connect encore créé
        setAccountStatus(null);
      } else {
        setError(err.response?.data?.detail || 'Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOnboarding = async () => {
    try {
      setCreating(true);
      setError(null);

      const response = await apiService.post('/api/v1/stripe/connect/onboard', {
        refresh_url: `${window.location.origin}/admin/settings/stripe/refresh`,
        return_url: `${window.location.origin}/admin/settings/stripe/success`,
      });

      // Rediriger vers Stripe pour compléter l'onboarding
      window.location.href = response.data.onboarding_url;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
      setCreating(false);
    }
  };

  const handleRefreshOnboarding = async () => {
    try {
      setCreating(true);
      setError(null);

      const response = await apiService.post('/api/v1/stripe/connect/onboard', {
        refresh_url: `${window.location.origin}/admin/settings/stripe/refresh`,
        return_url: `${window.location.origin}/admin/settings/stripe/success`,
      });

      window.location.href = response.data.onboarding_url;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du rafraîchissement');
      setCreating(false);
    }
  };

  const handleOpenDashboard = () => {
    if (accountStatus?.stripe_account_id) {
      window.open(`https://dashboard.stripe.com/${accountStatus.stripe_account_id}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto mt-8 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl mt-8 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" onClick={() => navigate('/admin/settings')}>
          <ArrowLeft size={18} className="mr-2" />
          Retour aux paramètres
        </Button>
        <h1 className="text-3xl font-bold text-foreground">
          Paiements Stripe Connect
        </h1>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="text-sm underline">Fermer</button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6 border-success/40 bg-success/10">
          <AlertDescription className="flex items-center justify-between text-success">
            {success}
            <button onClick={() => setSuccess(null)} className="text-sm underline">Fermer</button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Introduction */}
            <div>
              <h2 className="text-xl font-semibold mb-3">
                Configuration des paiements
              </h2>
              <p className="text-sm text-muted-foreground">
                Connecte ton compte Stripe pour recevoir les paiements des inscriptions à tes événements.
                LogFit® prélève une commission de 10% sur chaque inscription, le reste t'est directement transféré.
              </p>
            </div>

            <Separator />

            {/* État du compte */}
            {accountStatus ? (
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  État de ton compte Stripe
                </h3>

                <div className="space-y-3">
                  {/* ID du compte */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground min-w-[200px]">
                      ID du compte :
                    </span>
                    <span className="text-sm font-bold text-foreground">
                      {accountStatus.stripe_account_id || 'Non disponible'}
                    </span>
                  </div>

                  {/* Onboarding complété */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground min-w-[200px]">
                      Onboarding complété :
                    </span>
                    <Badge className={accountStatus.onboarding_completed ? 'bg-success/20 text-success border-success/40' : 'bg-warning/20 text-warning border-warning/40'}>
                      {accountStatus.onboarding_completed ? <CheckCircle size={14} className="mr-1" /> : <AlertTriangle size={14} className="mr-1" />}
                      {accountStatus.onboarding_completed ? 'Oui' : 'Non'}
                    </Badge>
                  </div>

                  {/* Détails soumis */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground min-w-[200px]">
                      Détails soumis :
                    </span>
                    <Badge className={accountStatus.details_submitted ? 'bg-success/20 text-success border-success/40' : 'bg-warning/20 text-warning border-warning/40'}>
                      {accountStatus.details_submitted ? <CheckCircle size={14} className="mr-1" /> : <AlertTriangle size={14} className="mr-1" />}
                      {accountStatus.details_submitted ? 'Oui' : 'Non'}
                    </Badge>
                  </div>

                  {/* Paiements activés */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground min-w-[200px]">
                      Paiements activés :
                    </span>
                    <Badge className={accountStatus.charges_enabled ? 'bg-success/20 text-success border-success/40' : 'bg-destructive/20 text-destructive border-destructive/40'}>
                      {accountStatus.charges_enabled ? <CheckCircle size={14} className="mr-1" /> : <AlertTriangle size={14} className="mr-1" />}
                      {accountStatus.charges_enabled ? 'Oui' : 'Non'}
                    </Badge>
                  </div>

                  {/* Transferts activés */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground min-w-[200px]">
                      Transferts activés :
                    </span>
                    <Badge className={accountStatus.payouts_enabled ? 'bg-success/20 text-success border-success/40' : 'bg-destructive/20 text-destructive border-destructive/40'}>
                      {accountStatus.payouts_enabled ? <CheckCircle size={14} className="mr-1" /> : <AlertTriangle size={14} className="mr-1" />}
                      {accountStatus.payouts_enabled ? 'Oui' : 'Non'}
                    </Badge>
                  </div>
                </div>

                <Separator className="my-6" />

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  {!accountStatus.onboarding_completed && (
                    <Button
                      onClick={handleRefreshOnboarding}
                      disabled={creating}
                    >
                      {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {creating ? 'Redirection...' : 'Compléter l\'onboarding'}
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={handleOpenDashboard}
                  >
                    <ExternalLink size={18} className="mr-2" />
                    Ouvrir le Dashboard Stripe
                  </Button>

                  <Button
                    variant="outline"
                    onClick={loadAccountStatus}
                  >
                    Rafraîchir le statut
                  </Button>
                </div>

                {/* Message d'information */}
                {(!accountStatus.charges_enabled || !accountStatus.payouts_enabled) && (
                  <Alert className="mt-6 border-warning/40 bg-warning/10">
                    <AlertDescription className="text-sm">
                      {!accountStatus.onboarding_completed
                        ? 'Tu dois compléter l\'onboarding Stripe pour pouvoir recevoir des paiements.'
                        : 'Stripe est en train de vérifier ton compte. Cela peut prendre quelques minutes à quelques heures.'}
                    </AlertDescription>
                  </Alert>
                )}

                {accountStatus.charges_enabled && accountStatus.payouts_enabled && (
                  <Alert className="mt-6 border-success/40 bg-success/10">
                    <AlertDescription className="text-sm text-success">
                      ✅ Ton compte est entièrement configuré et prêt à recevoir des paiements !
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Aucun compte Stripe connecté
                </h3>

                <p className="text-sm text-muted-foreground mb-4">
                  Tu dois créer un compte Stripe Connect pour commencer à recevoir des paiements.
                  Le processus est simple et sécurisé, géré directement par Stripe.
                </p>

                <Alert className="mb-6 border-info/40 bg-info/10">
                  <AlertDescription className="text-sm">
                    <strong>Ce qui te sera demandé :</strong>
                    <ul className="mt-2 pl-5 list-disc space-y-1">
                      <li>Informations sur ton organisation</li>
                      <li>Coordonnées bancaires (IBAN)</li>
                      <li>Document d'identité (pour la vérification)</li>
                      <li>Informations fiscales (SIRET si applicable)</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                <Button
                  size="lg"
                  onClick={handleCreateOnboarding}
                  disabled={creating}
                  className="px-6"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {creating ? 'Redirection...' : 'Créer mon compte Stripe Connect'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StripeOnboarding;
